import type { TimelineStop } from '../trip-timeline.api';
import { stopHeading } from '../policies/timeline-labels.policy';
import { TimelineActualRow } from './TimelineActualRow';

interface TimelineStopBlockProps {
  stop: TimelineStop;
  timezone: string;
  timeText: (iso: string) => string;
}

function PendingArrival({ stop, timeText }: Pick<TimelineStopBlockProps, 'stop' | 'timeText'>) {
  const planned = stop.planned.arrivalAt;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <span className="w-28 shrink-0 font-medium text-fg">Arrived</span>
      <span className="text-muted">{planned ? `plan ${timeText(planned)}` : 'no planned time'}</span>
      <span className="text-muted">not yet</span>
    </li>
  );
}

/** The yard is where the trip starts from. Nothing records leaving it, so it shows the planned start and says there is no actual. */
function YardRow({ stop, timeText }: Pick<TimelineStopBlockProps, 'stop' | 'timeText'>) {
  const planned = stop.planned.arrivalAt;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
      <span className="text-muted">{planned ? `planned start ${timeText(planned)}` : 'no planned start'}</span>
      <span className="text-muted">no actual recorded</span>
    </li>
  );
}

/** One plan stop with its planned arrival beside what was recorded. A stack, not a table, so it needs no sideways scroll on a phone. */
export function TimelineStopBlock({ stop, timezone, timeText }: TimelineStopBlockProps) {
  const arrival = stop.actuals.find((actual) => actual.milestone === 'arrived');
  const isYard = stop.kind === 'yard';
  return (
    <section aria-label={stopHeading(stop)} className="rounded-lg border border-line bg-surface p-4">
      <h3 className="mb-2 text-sm font-semibold text-fg">{stopHeading(stop)}</h3>
      <ul className="flex flex-col gap-2">
        {isYard && <YardRow stop={stop} timeText={timeText} />}
        {!isYard && !arrival && <PendingArrival stop={stop} timeText={timeText} />}
        {stop.actuals.map((actual) => (
          <TimelineActualRow
            key={actual.milestone}
            actual={actual}
            timezone={timezone}
            timeText={timeText}
            plan={actual === arrival ? { plannedAt: stop.planned.arrivalAt, varianceSeconds: stop.variance.arrivalSeconds } : undefined}
          />
        ))}
      </ul>
    </section>
  );
}
